import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import { TimeFrameKey } from 'data/time-frames';
import tradeStatus from 'data/trade';
import { usePortfolioPerformanceGraph } from 'hooks/products/useGraphs';
import { useFunctionalCurrency } from 'hooks/products/useProducts';
import moment from 'moment';
import React from 'react';
import { usePortfolioGainsAndLosses } from 'store/slices/gains-api';
import formatNumber from 'utils/format-number';
import getTickerIntervalSnapValue from 'utils/format-performance-tickers';
import PerformanceGraphView from './PerformanceGraphView';

export default function PerformanceGraph({
  timeFrame,
  headerControls,
}: {
  timeFrame: TimeFrameKey;
  headerControls?: React.ReactElement;
}) {
  const { functionalCurrency } = useFunctionalCurrency();
  const { graph: performance, isLoading } =
    usePortfolioPerformanceGraph(timeFrame);
  const { gainsAndLosses } = usePortfolioGainsAndLosses(timeFrame);

  const values = performance?.map(v => v.value);
  const max = values && Math.max(...values);
  const min = values && Math.min(...values);

  if (!functionalCurrency) return null;

  const yAxisTickAmount = 10;

  const yAxisMin =
    max === undefined || max === null || min === undefined || min === null
      ? 0
      : min;

  const yAxisMax =
    max === undefined || max === null || min === undefined || min === null
      ? 400
      : max;
  const yTickInterval = getTickerIntervalSnapValue(
    max === undefined || min === undefined
      ? 100
      : (max - min) / (yAxisTickAmount / 2),
  );

  const valueFormat = (function getValueFormat(): (value: number) => string {
    const def = (value: number) =>
      `${functionalCurrency.symbol} ${formatNumber(value, 0, false, true, false)}`;

    if (
      max === undefined ||
      max === null ||
      min === undefined ||
      min === null
    ) {
      return def;
    }
    if (max > 9999999) {
      return value =>
        `${
          value === 0
            ? formatNumber(value / 1000000, 0)
            : `${formatNumber(value / 1000000, 0)}m`
        }`;
    }
    if (max > 9999) {
      return value =>
        `${
          value === 0
            ? formatNumber(value / 1000, 0)
            : `${formatNumber(value / 1000, yTickInterval < 1000 ? 1 : 0)}k`
        }`;
    }
    return def;
  })();

  const tooltipValueFormat = (function getValueFormat(): (
    value: number,
  ) => string {
    return (value: number) =>
      `${functionalCurrency.symbol} ${formatNumber(
        value,
        tradeStatus.FiatDecimals,
        false,
        true,
        false,
      )}`;
  })();

  const [xTickInterval, dateFormat] = (function getTimeframeOptions(): [
    number | undefined,
    (value: number) => string,
  ] {
    const hours = 1000 * 60 * 60;
    if (timeFrame === '1y') {
      return [
        hours * 24 * 30.5,
        (value: number) => moment(value).format("D MMM 'YY"),
      ];
    }
    if (timeFrame === '3m') {
      return [
        hours * 24 * 7 * 3,
        (value: number) => moment(value).format("D MMM 'YY"),
      ];
    }
    if (timeFrame === '1m') {
      return [
        hours * 24 * 7,
        (value: number) => moment(value).format("D MMM 'YY"),
      ];
    }
    if (timeFrame === '7d') {
      return [hours * 24, (value: number) => moment(value).format('D MMM')];
    }
    if (timeFrame === '24h') {
      return [hours * 4, (value: number) => moment(value).format('HH:mm')];
    }
    return [undefined, (value: number) => moment(value).format("D MMM 'YY")];
  })();

  if (!performance) {
    return (
      <div className="h-full w-full rounded-card py-3 px-5 flex flex-col flex justify-center text-header-text bg-background">
        <CustomLoader />
      </div>
    );
  }

  return (
    <>
      <div className="h-full w-full bg-background flex flex-col flex-1 rounded-lg overflow-hidden">
        <h2>Show Graph</h2>
      </div>
      <PerformanceGraphView
        headerControls={headerControls}
        data={
          performance && performance.map(d => [d.timestamp.getTime(), d.value])
        }
        portfolioGainsPercentage={gainsAndLosses?.gainPercentage!}
        xAxis={{
          tickInterval: xTickInterval,
          formatter(value) {
            return `<span style="font-family: Bozon, sans-serif" class="text-header-text opacity-50">${dateFormat(
              value,
            )}</span>`;
          },
        }}
        yAxis={{
          tickInterval: yTickInterval,
          labels: {
            enabled: true,
            useHTML: true,
            overflow: 'allow',
            align: 'right',
            x: max !== undefined && max > 9999 ? (max > 99999 ? 35 : 38) : 56,
            formatter(label) {
              return `<span style="font-family: Bozon, sans-serif" class="text-header-text opacity-50">${
                performance ? valueFormat(Number(label.value)) : ''
              }</span>`;
            },
            style: {
              fontFamily: 'Bozon',
              whiteSpace: 'nowrap',
            },
          },
          min: yAxisMin,
          max: yAxisMax,
        }}
        tooltipFormatter={(x, y) => {
          return `
                <div class="background-blur p-2 bg-white bg-opacity-25 rounded-lg text-white">
                    <div style="font-family: Bozon" class="text-lg font-semibold">${tooltipValueFormat(
                      y,
                    )}</div>
                    <div style="font-family: Bozon" class="">
                    ${dateFormat(x)}
                    </div>
                </div>
            `;
        }}
        isGreaterThanHundredThousand={max !== undefined && max > 9999}
      />
    </>
  );
}
